"use client"

import { Form, Formik } from "formik"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { FormChannelSelect } from "@/components/form-inputs"
import { DestinationList } from "@/components/notification-channels/destination-list"
import { buildDestinations, type DestinationCandidate } from "@/components/notification-channels/destinations"
import { deliveryWarning } from "@/lib/notifications/recipients"
import { useUpdateSystemNotificationSubscriptionMutation } from "@/hooks/mutations/system-notifications"
import type {
  SystemNotificationChannelView,
  SystemNotificationEventView,
} from "@/server-actions/system-notifications"
import { NotificationRow } from "./notification-row"
import { useTranslations } from "next-intl"


interface SystemEventRowProps {
  event: SystemNotificationEventView
  channels: SystemNotificationChannelView[]
  fallbackEmail: string
}

/** A platform event: the switch saves at once; while it is on, its channels sit under the row. */
export function SystemEventRow({ event, channels, fallbackEmail }: SystemEventRowProps) {
  const tUi = useTranslations("SystemNotifications.SystemNotificationsSystemEventRow")

  // Per row: a shared mutation tracks only the latest save and re-enables a row still saving.
  const update = useUpdateSystemNotificationSubscriptionMutation()
  const saving = update.isPending
  const onSave = (values: { enabled: boolean; channelIds: string[] }) =>
    update.mutateAsync({ eventType: event.eventType, ...values }).catch(() => {})
  const { destinations, systemChannelMissing } = buildDestinations(toCandidates(channels), event.channelIds)

  return (
    <NotificationRow
      label={event.label}
      description={event.description}
      badges={!event.enabled && <Badge variant="outline"><span key={tUi("off")}>{tUi("off")}</span></Badge>}
      toggle={{
        label: `Send "${event.label}"`,
        enabled: event.enabled,
        disabled: saving,
        onChange: (next) => void onSave({ enabled: next, channelIds: event.channelIds }),
      }}
    >
      {event.enabled && (
        <div className="space-y-3">
          <Formik
            enableReinitialize
            initialValues={{ channelIds: event.channelIds }}
            onSubmit={(values) => onSave({ enabled: true, channelIds: values.channelIds })}
          >
            {({ dirty, isSubmitting }) => (
              <Form className="flex flex-wrap items-center gap-2">
                <FormChannelSelect
                  name="channelIds"
                  options={channels}
                  emptyLabel="System email (default)"
                  disabled={saving}
                />
                {dirty && (
                  <Button type="submit" size="sm" disabled={saving || isSubmitting}>
                    <span key={tUi("save")}>{tUi("save")}</span></Button>
                )}
              </Form>
            )}
          </Formik>
          <DestinationList
            destinations={destinations}
            emptyFallbackNote={fallbackNote(systemChannelMissing, fallbackEmail)}
          />
        </div>
      )}
    </NotificationRow>
  )
}

function fallbackNote(systemChannelMissing: boolean, fallbackEmail: string): string | undefined {
  if (!systemChannelMissing) return undefined
  if (fallbackEmail) return `Falling back to ${fallbackEmail}, the platform tenant owner.`
  return "Nowhere — select a working channel, or give the platform tenant owner an email address."
}

function toCandidates(channels: SystemNotificationChannelView[]): DestinationCandidate[] {
  return channels.map((channel) => ({
    id: channel.id,
    type: channel.type,
    name: channel.name,
    isSystem: channel.isSystem,
    recipientsShort: channel.recipientsShort,
    recipientsFull: channel.recipientsFull,
    warning: deliveryWarning(channel),
  }))
}
